/** @flow */
import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import uiSchema from "./__ui__/schema";
import { SLA_TYPES_WITH_DISABLED_MEASURE } from "@modules/client/constants/";
import actionCreators from "@modules/client/actionCreator";
import * as clientsSelectors from "@modules/client/selectors";
import { Modal, ModalHeader, ModalBody, Button, WarningBox, ModalFooter, Col } from "@components";
import { SchemaForm } from "@components";
import type { State } from "@store/reducers";
import type { SlaAgreement } from "@modules/client/reducer/sla/types";

type PropsFromState = $Exact<{
  agreements: SlaAgreement[],
  activeSlaModal: string,
  success: string,
}>;

type PropsFromDispatch = $Exact<{
  onChangeSla: typeof actionCreators.PutAgreements,
  toggleSlaClientModal: typeof actionCreators.ToggleSlaClientModal,
}>;
type Props = $Exact<{
  ...PropsFromLocalize,
  ...PropsFromDispatch,
  ...PropsFromState,
  clientId: number,
  schema: Object,
}>;

export class ChangeSlaModal extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    // Class components needs constructor for accessing the SchemaForm's ref
    this.formRef = React.createRef();
  }

  submitForm = () => {
    this.formRef.current.submit();
  };

  get relevantAgreement() {
    return (
      this.props.agreements.find(a => a.sla.name === this.props.activeSlaModal) || {
        period: null,
        sla: {
          name: null,
          displayName: null,
          description: null,
          values: [],
        },
        value: null,
        isEnabled: null,
        measure: null,
      }
    );
  }

  get schema() {
    const { schema } = this.props;
    const {
      sla: { values = [] },
    } = this.relevantAgreement;

    return !schema
      ? null
      : {
          ...schema,
          properties: {
            ...(schema || {}).properties,
            value: {
              ...(schema || {}).properties.value,
              ...(values.length
                ? {
                    oneOf: values.sort().map(v => ({
                      const: v,
                      title: `${v}`,
                    })),
                  }
                : {}),
            },
          },
        };
  }

  /**
   * @returns uiSchema with or without hidden measure widget
   */
  get filterUiSchema() {
    let result = uiSchema;

    if (SLA_TYPES_WITH_DISABLED_MEASURE.indexOf(this.props.activeSlaModal) !== -1) {
      result = { ...uiSchema, measure: { "ui:widget": "hidden" } };
    }

    return result;
  }

  parseFormData() {
    return {
      period: this.relevantAgreement.period || null,
      value: this.relevantAgreement.value || null,
      measure: this.relevantAgreement.measure || null,
      isEnabled: this.relevantAgreement.isEnabled || false,
    };
  }
  handleCancel = () => {
    this.props.toggleSlaClientModal(false);
  };
  render() {
    return (
      <Modal className="modal-small">
        <ModalHeader tag="h3" toggle={this.handleCancel}>
          {this.props.t("ADJUST_SLA_MEASURES")}
        </ModalHeader>
        <ModalBody>
          {this.props.success && <WarningBox type="success">{this.props.success}</WarningBox>}
          {this.props.schema ? (
            <SchemaForm
              externalSubmit
              schema={this.schema}
              formData={this.parseFormData()}
              ref={this.formRef}
              onSubmit={formData => {
                this.props.onChangeSla({
                  formData,
                  clientId: this.props.clientId,
                  slaKeyId: this.props.activeSlaModal,
                });
              }}
              uiSchema={this.filterUiSchema}
            />
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button materialIcon="replay_circle_filled" onClick={this.submitForm}>
            {this.props.t("SUBMIT")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  activeSlaModal: clientsSelectors.getActiveSlaModal(state),
  agreements: clientsSelectors.getAgreements(state),
  success: clientsSelectors.getSuccessMessage(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  toggleSlaClientModal: actionCreators.ToggleSlaClientModal,
  onChangeSla: actionCreators.PutAgreements,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(ChangeSlaModal));
